import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight, Tag } from "lucide-react";
import { blogPosts } from "../data/blogPosts";

const categoryColors = {
  "Water Treatment": "bg-cyan-500/10 text-cyan-300 border-cyan-400/20",
  "Arsenic Removal": "bg-red-500/10 text-red-300 border-red-400/20",
  "Water Purification": "bg-blue-500/10 text-blue-300 border-blue-400/20",
  "Iron Removal": "bg-orange-500/10 text-orange-300 border-orange-400/20",
  "Softener Plant": "bg-emerald-500/10 text-emerald-300 border-emerald-400/20",
  "Water Filter": "bg-violet-500/10 text-violet-300 border-violet-400/20",
};

export default function BlogPage() {
  const [featured, ...rest] = blogPosts;

  return (
    <div className="mx-auto max-w-7xl px-6 py-20">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="mx-auto max-w-3xl text-center"
      >
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-400">
          Blog &amp; Guides
        </p>
        <h1 className="mt-4 text-4xl font-bold md:text-6xl">
          Water Treatment Insights for Kolkata, Newtown &amp; Rajarhat
        </h1>
        <p className="mt-6 text-lg leading-8 text-slate-300">
          Practical guides on RO plants, UV systems, arsenic removal, iron
          removal, softener plants and water filters for residential properties
          across Kolkata and West Bengal.
        </p>
      </motion.div>

      {/* Featured post */}
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="mt-16"
      >
        <Link to={`/blog/${featured.slug}`} className="group block overflow-hidden rounded-[2rem] border border-white/10 bg-white/5 backdrop-blur-xl transition hover:border-cyan-400/30">
          <div className="grid lg:grid-cols-2">
            <div className="relative h-72 overflow-hidden lg:h-auto">
              <img
                src={featured.image}
                alt={featured.title}
                className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-slate-950/60 lg:block hidden" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 to-transparent lg:hidden" />
            </div>

            <div className="flex flex-col justify-center p-8 md:p-10">
              <div className="mb-4 flex items-center gap-3">
                <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${categoryColors[featured.category] ?? "bg-cyan-500/10 text-cyan-300 border-cyan-400/20"}`}>
                  <Tag size={11} />
                  {featured.category}
                </span>
                <span className="text-xs text-slate-500">Featured</span>
              </div>

              <h2 className="text-2xl font-bold leading-snug md:text-3xl group-hover:text-cyan-300 transition">
                {featured.title}
              </h2>
              <p className="mt-4 leading-7 text-slate-400">{featured.excerpt}</p>

              <div className="mt-6 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Calendar size={14} />
                  {featured.date}
                </div>
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-400 group-hover:gap-3 transition-all">
                  Read Article <ArrowRight size={15} />
                </span>
              </div>
            </div>
          </div>
        </Link>
      </motion.div>

      {/* Rest of posts grid */}
      <div className="mt-10 grid gap-7 md:grid-cols-2 xl:grid-cols-3">
        {rest.map((post, index) => (
          <motion.div
            key={post.id}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.55, delay: index * 0.07 }}
          >
            <Link
              to={`/blog/${post.slug}`}
              className="group flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl transition hover:border-cyan-400/30"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />
                <span className={`absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold backdrop-blur ${categoryColors[post.category] ?? "bg-cyan-500/10 text-cyan-300 border-cyan-400/20"}`}>
                  <Tag size={11} />
                  {post.category}
                </span>
              </div>

              <div className="flex flex-1 flex-col p-6">
                <h2 className="text-lg font-bold leading-snug group-hover:text-cyan-300 transition">
                  {post.title}
                </h2>
                <p className="mt-3 flex-1 text-sm leading-6 text-slate-400">
                  {post.excerpt}
                </p>
                <div className="mt-5 flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    <Calendar size={13} />
                    {post.date}
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-400 group-hover:gap-2.5 transition-all">
                    Read <ArrowRight size={13} />
                  </span>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6 }}
        className="mt-20 rounded-[2rem] border border-white/10 bg-gradient-to-r from-cyan-500/10 via-slate-900/70 to-emerald-500/10 p-8 text-center md:p-12"
      >
        <h2 className="text-3xl font-bold md:text-4xl">
          Need a Water Treatment Solution in Kolkata?
        </h2>
        <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-slate-300">
          Talk to our team for a free water test and custom proposal — RO
          plants, UV systems, arsenic removal, iron removal, softener plants
          and water filters across Kolkata, Newtown and Rajarhat.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
          <Link
            to="/contact"
            className="rounded-2xl bg-cyan-500 px-7 py-3.5 font-semibold text-slate-950 transition hover:bg-cyan-400"
          >
            Get Free Proposal
          </Link>
          <a
            href="tel:+918697702088"
            className="rounded-2xl border border-white/15 bg-white/5 px-7 py-3.5 font-semibold transition hover:bg-white/10"
          >
            Call 8697702088
          </a>
        </div>
      </motion.div>
    </div>
  );
}
